import React, { useState, useEffect } from 'react'
import {
  CurrencyDollarIcon,
  CalendarIcon,
  UserIcon,
  CheckCircleIcon,
  ClockIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  FunnelIcon
} from '@heroicons/react/24/outline'
import { financeiroAPI } from '@/services/api'

interface Commission {
  id: number
  seller_id: number
  seller_name: string
  sale_id: number
  sale_description: string
  sale_amount: number
  commission_rate: number
  commission_amount: number
  status: 'pending' | 'approved' | 'paid'
  created_at: string
  paid_at?: string
}

interface CommissionSummary {
  total_pending: number
  total_approved: number
  total_paid: number
  count_pending: number
  count_approved: number
  count_paid: number
}

const CommissionsPage: React.FC = () => {
  const [commissions, setCommissions] = useState<Commission[]>([])
  const [sellers, setSellers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [summary, setSummary] = useState<CommissionSummary>({
    total_pending: 0,
    total_approved: 0,
    total_paid: 0,
    count_pending: 0,
    count_approved: 0,
    count_paid: 0
  })

  // Filters
  const [selectedSeller, setSelectedSeller] = useState<string>('')
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [sellersData] = await Promise.all([
        financeiroAPI.vendedores.list()
      ])
      setSellers(sellersData)

      // Simular comissões baseadas nas vendas
      // TODO: Criar endpoint real de comissões
      const mockCommissions: Commission[] = sellersData.flatMap((seller: any, idx: number) => {
        if (idx < 3) {
          return [
            {
              id: idx * 2 + 1,
              seller_id: seller.id,
              seller_name: seller.name || seller.nome,
              sale_id: 100 + idx,
              sale_description: `Venda #${100 + idx}`,
              sale_amount: Math.floor(Math.random() * 5000) + 500,
              commission_rate: 10,
              commission_amount: 0,
              status: ['pending', 'approved', 'paid'][idx % 3] as any,
              created_at: new Date().toISOString()
            }
          ]
        }
        return []
      }).map(c => ({
        ...c,
        commission_amount: c.sale_amount * (c.commission_rate / 100)
      }))

      setCommissions(mockCommissions)

      // Calculate summary
      const newSummary = mockCommissions.reduce((acc, c) => {
        if (c.status === 'pending') {
          acc.total_pending += c.commission_amount
          acc.count_pending++
        } else if (c.status === 'approved') {
          acc.total_approved += c.commission_amount
          acc.count_approved++
        } else if (c.status === 'paid') {
          acc.total_paid += c.commission_amount
          acc.count_paid++
        }
        return acc
      }, {
        total_pending: 0,
        total_approved: 0,
        total_paid: 0,
        count_pending: 0,
        count_approved: 0,
        count_paid: 0
      })

      setSummary(newSummary)
    } catch (err) {
      console.error('Erro ao carregar dados:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
            <ClockIcon className="w-3 h-3" />
            Pendente
          </span>
        )
      case 'approved':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 flex items-center gap-1">
            <CheckCircleIcon className="w-3 h-3" />
            Aprovada
          </span>
        )
      case 'paid':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 flex items-center gap-1">
            <BanknotesIcon className="w-3 h-3" />
            Paga
          </span>
        )
      default:
        return null
    }
  }

  const filteredCommissions = commissions.filter(c => {
    if (selectedSeller && c.seller_id.toString() !== selectedSeller) return false
    if (selectedStatus && c.status !== selectedStatus) return false
    return true
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <CurrencyDollarIcon className="w-8 h-8 text-green-600" />
          Comissoes de Vendas
        </h1>
        <p className="text-gray-600 mt-1">Acompanhe e gerencie comissoes da equipe de vendas</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pendentes</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total_pending)}</p>
              <p className="text-xs text-gray-500">{summary.count_pending} comissoes</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Aprovadas</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total_approved)}</p>
              <p className="text-xs text-gray-500">{summary.count_approved} comissoes</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <BanknotesIcon className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pagas</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total_paid)}</p>
              <p className="text-xs text-gray-500">{summary.count_paid} comissoes</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <ArrowTrendingUpIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Geral</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(summary.total_pending + summary.total_approved + summary.total_paid)}
              </p>
              <p className="text-xs text-gray-500">
                {summary.count_pending + summary.count_approved + summary.count_paid} comissoes
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="w-5 h-5 text-gray-500" />
          <span className="font-medium text-gray-700">Filtros</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor</label>
            <select
              value={selectedSeller}
              onChange={(e) => setSelectedSeller(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              {sellers.map((seller) => (
                <option key={seller.id} value={seller.id}>
                  {seller.name || seller.nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="pending">Pendente</option>
              <option value="approved">Aprovada</option>
              <option value="paid">Paga</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Inicial</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Final</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Commissions Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendedor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Venda
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor Venda
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taxa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comissao
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCommissions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    <CurrencyDollarIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Nenhuma comissao encontrada</p>
                  </td>
                </tr>
              ) : (
                filteredCommissions.map((commission) => (
                  <tr key={commission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <UserIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{commission.seller_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {commission.sale_description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(commission.sale_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {commission.commission_rate}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                      {formatCurrency(commission.commission_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(commission.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(commission.created_at).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default CommissionsPage
